import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';

/**
 * TuningExplainer — Always shows the full parameter table.
 * Highlights rows where Tier 2 tuning changed a value.
 * Shows "why" explanations from diffs when available.
 *
 * Props:
 *   explanations — full list of {parameter, default_value, tuned_value}
 *   diffs — list of changed params with {parameter, explanation}
 */
export default function TuningExplainer({ explanations = [], diffs = [] }) {
  // Build lookup of diff explanations by parameter name
  const diffMap = {};
  for (const d of diffs) {
    diffMap[d.parameter] = d.explanation;
  }

  return (
    <TableContainer sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, color: vortxColors.gold, bgcolor: 'transparent', borderBottom: `1px solid ${vortxColors.stone}40` }}>Parameter</TableCell>
            <TableCell sx={{ fontWeight: 600, color: vortxColors.gold, bgcolor: 'transparent', borderBottom: `1px solid ${vortxColors.stone}40` }}>Default</TableCell>
            <TableCell sx={{ fontWeight: 600, color: vortxColors.gold, bgcolor: 'transparent', borderBottom: `1px solid ${vortxColors.stone}40` }}>Site-Tuned</TableCell>
            <TableCell sx={{ fontWeight: 600, color: vortxColors.gold, bgcolor: 'transparent', borderBottom: `1px solid ${vortxColors.stone}40` }}>Why</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {explanations.map((row, idx) => {
            const changed = String(row.default_value) !== String(row.tuned_value);
            const why = diffMap[row.parameter] || '';
            return (
              <TableRow
                key={row.parameter || idx}
                sx={changed ? { backgroundColor: `${vortxColors.gold}10` } : undefined}
              >
                <TableCell sx={{ fontWeight: changed ? 600 : 400, color: vortxColors.white, borderBottom: `1px solid ${vortxColors.stone}20` }}>
                  {row.parameter}
                </TableCell>
                <TableCell sx={{ color: vortxColors.stone, borderBottom: `1px solid ${vortxColors.stone}20` }}>
                  {formatValue(row.default_value)}
                </TableCell>
                <TableCell sx={{ color: changed ? vortxColors.gold : vortxColors.white, fontWeight: changed ? 600 : 400, borderBottom: `1px solid ${vortxColors.stone}20` }}>
                  {formatValue(row.tuned_value)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: vortxColors.stone, borderBottom: `1px solid ${vortxColors.stone}20`, maxWidth: 200 }}>
                  {why || (changed ? '' : 'No change')}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function formatValue(val) {
  if (val == null) return '\u2014';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'number') return Number.isInteger(val) ? String(val) : val.toFixed(2);
  return String(val);
}
